<template>
  <v-card class="modal">
    <div class="modal-cabecera">
      <div>
        <div class="modal-etiqueta">
          <v-icon size="12">
            mdi-store-plus-outline
          </v-icon>
          Nueva
        </div>
        <h2 class="modal-titulo">
          Registra tu empresa
        </h2>
      </div>
      <v-btn
        icon
        variant="text"
        size="small"
        class="modal-cerrar"
        @click="close()"
      >
        <v-icon>mdi-close</v-icon>
      </v-btn>
    </div>

    <v-card-text class="modal-cuerpo">
      <div class="modal-grupo">
        Datos básicos
      </div>
      <v-text-field
        v-model="company"
        :error-messages="errorNombre"
        class="fg-blue"
        label="Nombre de la empresa"
        variant="outlined"
        density="compact"
        maxlength="255"
      />
      <v-textarea
        v-model="description"
        class="fg-blue mt-2"
        label="Descripción"
        variant="outlined"
        density="compact"
        rows="3"
        :counter="1200"
        maxlength="1200"
        hint="Qué vendes y qué te hace distinto"
      />

      <div class="modal-grupo mt-5">
        Ubicación
      </div>
      <div class="modal-grid-2">
        <v-text-field
          v-model="pais"
          class="fg-blue"
          label="País"
          variant="outlined"
          density="compact"
          maxlength="255"
        />
        <v-text-field
          v-model="city"
          class="fg-blue"
          label="Ciudad"
          variant="outlined"
          density="compact"
          maxlength="255"
        />
      </div>
      <v-text-field
        v-model="location"
        class="fg-blue mt-2"
        label="Dirección"
        variant="outlined"
        density="compact"
        maxlength="500"
      />

      <div class="modal-grupo mt-5">
        Contacto
      </div>
      <v-text-field
        v-model="phone"
        :error-messages="errorFonoPrincipal"
        type="tel"
        inputmode="tel"
        class="fg-blue"
        label="Teléfono"
        variant="outlined"
        density="compact"
        maxlength="30"
      />

      <v-switch
        v-model="receivesWhatsapp"
        class="mb-1"
        color="primary"
        density="compact"
        label="Recibimos mensajes por WhatsApp"
        hide-details
      />
      <v-text-field
        v-if="receivesWhatsapp"
        v-model="whatsapp"
        :error-messages="errorFonoWhatsapp"
        type="tel"
        inputmode="tel"
        class="fg-blue mt-2"
        label="Número de WhatsApp"
        variant="outlined"
        density="compact"
        maxlength="30"
        hint="Con indicativo si es de otro país. Ej: 3204720783"
        persistent-hint
      />

      <div class="modal-grupo mt-5">
        Reservas
      </div>
      <v-switch
        v-model="acceptsReservations"
        color="primary"
        density="compact"
        label="Esta empresa acepta reservas"
        hide-details
      />
      <p class="modal-ayuda">
        Actívalo si tus clientes apartan mesa, cita o turno. Podrás gestionarlas
        desde la pestaña Reservas.
      </p>

      <v-alert
        v-if="error"
        type="error"
        variant="tonal"
        density="comfortable"
        class="mt-4"
        closable
        @click:close="error = null"
      >
        {{ error }}
      </v-alert>
    </v-card-text>

    <v-card-actions class="modal-pie">
      <v-spacer />
      <v-btn
        variant="text"
        :disabled="guardando"
        @click="close()"
      >
        Cancelar
      </v-btn>
      <v-btn
        color="primary"
        variant="flat"
        :loading="guardando"
        @click="save()"
      >
        <v-icon start>
          mdi-content-save
        </v-icon>
        Registrar empresa
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import apisCompanys from '@/apis/apisCompanys';
import { mensajeDeError } from '@/utils/errores';
import { limpiarTelefono, errorTelefono } from '@/utils/telefono';

/*
  alRegistrarCorrectamente(company)
  onClose
*/
export default {
  name: 'NewCompany',
  emits: ["alRegistrarCorrectamente", "onClose"],
  data: function(){
    return {
      company: "",
      pais: "",
      city: "",
      description: "",
      location: "",
      phone: "",
      receivesWhatsapp: false,
      whatsapp: "",
      acceptsReservations: false,
      intentoGuardar: false,
      error: null,
      guardando: false
    }
  },
  computed: {
    // Los errores solo salen despues del primer intento: reganar por un campo
    // que el usuario todavia no ha tocado es peor que no avisar
    errorNombre(){
      if (!this.intentoGuardar) {
        return []
      }
      return this.company.trim() ? [] : ['El nombre es obligatorio']
    },
    errorFonoPrincipal(){
      if (!this.intentoGuardar && !this.phone) {
        return []
      }
      return errorTelefono(this.phone)
    },
    errorFonoWhatsapp(){
      if (!this.receivesWhatsapp) {
        return []
      }
      return errorTelefono(this.whatsapp, {
        mensajeVacio: 'Escribe el número o desactiva la casilla'
      })
    }
  },
  watch: {
    // Se limpia mientras se escribe: las letras no llegan al backend ni
    // pegando desde el portapapeles
    phone(valor){
      const limpio = limpiarTelefono(valor)
      if (limpio !== valor) {
        this.phone = limpio
      }
    },
    whatsapp(valor){
      const limpio = limpiarTelefono(valor)
      if (limpio !== valor) {
        this.whatsapp = limpio
      }
    }
  },
  methods: {
    save(){
      this.intentoGuardar = true
      this.error = null

      const problema = this.errorNombre[0]
        || this.errorFonoPrincipal[0]
        || this.errorFonoWhatsapp[0]

      if (problema) {
        this.error = problema
        return
      }

      this.guardando = true

      apisCompanys.saveCompany({
        company: this.company.trim(),
        country: this.pais.trim(),
        city: this.city.trim(),
        description: this.description.trim(),
        location: this.location.trim(),
        phone: this.phone.trim(),
        // Si no recibe WhatsApp el numero no se manda: guardarlo igual deja un
        // dato que meses despues nadie sabe si sigue vigente
        receivesWhatsapp: this.receivesWhatsapp,
        whatsapp: this.receivesWhatsapp ? this.whatsapp.trim() : null,
        acceptsReservations: this.acceptsReservations
      })
        .then(res => {
          this.$emit('alRegistrarCorrectamente', res)
        })
        // Sin catch, si el registro fallaba no pasaba nada visible: el
        // dialogo se quedaba abierto y parecia que el boton no respondia
        .catch(error => {
          this.error = mensajeDeError(error, 'No se pudo registrar la empresa')
        })
        .finally(() => {
          this.guardando = false
        })
    },
    close(){
      this.$emit("onClose")
    }
  }
}
</script>

<style scoped>
/* El diseno del modal vive en styles/overwirthe.css, compartido con los
   demas modales. */
</style>
